/**
 * Plaid Service
 *
 * Thin public surface over the Plaid aggregation endpoints. The actual calls
 * live in portfolioService (which owns the portfolio API client); this module
 * gives the Plaid UI a dedicated, discoverable import.
 *
 * The backend (Spring Boot) owns the Plaid secret and access tokens. The
 * browser only ever handles the short-lived `link_token` and `public_token`.
 *
 * Contract: docs/plaid-architecture.md
 */
import { portfolioService } from './portfolioService';

export const plaidService = {
  /**
   * Create a Link token for the current user.
   * @param {string} userId
   * @returns {Promise<{linkToken: string}>}
   */
  createLinkToken: (userId) => portfolioService.createPlaidLinkToken(userId),

  /**
   * Exchange the public_token returned by Link for a stored access token and
   * persist the linked accounts.
   * @param {string} userId
   * @param {string} publicToken
   * @param {{institutionId?: string, institutionName?: string}} institution
   * @returns {Promise<{accounts: Array}>}
   */
  exchangePublicToken: (userId, publicToken, institution) =>
    portfolioService.exchangePlaidPublicToken(userId, publicToken, institution),

  /**
   * Sync (accounts + holdings + transactions) for all linked items.
   * @param {string} userId
   * @returns {Promise<{accounts: Array}>}
   */
  syncAccounts: (userId) => portfolioService.syncPlaidAccounts(userId),

  /**
   * User-triggered refresh (backend-enforced cooldown).
   * @param {string} userId
   * @returns {Promise<{accounts: Array}>}
   */
  refreshAccounts: (userId) => portfolioService.refreshPlaidAccounts(userId),

  /**
   * Create an update-mode Link token so the user can grant Statements consent
   * for an existing item (fixes ADDITIONAL_CONSENT_REQUIRED).
   * @param {string} itemId
   * @returns {Promise<{linkToken: string}>}
   */
  createStatementsConsentLinkToken: (itemId) =>
    portfolioService.createPlaidStatementsConsentLinkToken(itemId),
};

export default plaidService;
